import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  PrimaryButton,
  Table,
} from "widgets";
import {
  useFetchAllBanks,
  useFetchChequeAcknowledgement,
  useFetchOrderDetails,
  useMarkChequeReceived,
  useFetchOrderInvoice,
  useFetchPaymentReceipt,
  useFetchOrderSummaryPDF,
  useUpdateCashStatus,
} from "store/backend/assets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import Loader from "shared/Loader/Loader";
import { formatINRCurrency } from "utils/formatINRCurrency";
import {
  ADJUSTMENT_AGAINST_REFUNDS_PAYMENT_ID,
  POST_SUBMIT_RELOAD_DELAY_IN_MS,
  CHEQUE_STATUS_IDS,
  ORDER_STATUSES,
  PAYMENT_STATUS,
  SCA_VERTICAL_ID,
  SUCCESSFUL_ONLINE_TRANSACTION_STATUS_ID,
  SUCCESSFUL_PAYMENT_STATUS_IDS,
  CASH_PAYMENT_METHOD_ID,
} from "constants/constants";
import { formatReadableDate } from "utils/formatDate";
import DownloadInvoice from "containers/DownloadInvoice/DownloadInvoice";
import SecondaryButton from "widgets/Buttons/SecondaryButton";

import styles from "./OrderDetails.module.scss";

const getColumns = () => {
  return [
    { label: "Reference No.", field: "referenceNo" },
    { label: "Amount", field: "amount" },
    { label: "Status", field: "status" },
    { label: "Method", field: "method" },
    { label: "Payment Date", field: "payment_date" },
    { label: "Bank Details", field: "bank_details" },
    { label: "Action", field: "action" },
    { label: "Receipt", field: "receipt" },
    { label: "Invoice", field: "taxInvoice" },
  ];
};
const isReceiptDownloadable = (payment) => {
  if (!SUCCESSFUL_PAYMENT_STATUS_IDS.includes(payment.status.id)) {
    return false;
  }

  if (payment.loan_details && Object.keys(payment.loan_details).length !== 0) {
    return false;
  }

  return true;
};
const handleOnClickDownloadPaymentReceipt = (
  payment,
  fetchPaymentReceipt,
  openSnackbar
) => {
  fetchPaymentReceipt(payment.id)
    .then((response) => {
      const link = document.createElement("a");
      link.href = response.signedUrl;
      link.click();
      openSnackbar("Payment Receipt downloaded successfully!", "success");
    })
    .catch((error) => {
      openSnackbar(error.message || "Error fetching Payment Receipt.", "error");
    });
};

const handleOnClickDownloadOrderInvoice = (
  orderId,
  fetchOrderInvoice,
  openSnackbar
) => {
  fetchOrderInvoice(orderId)
    .then((response) => {
      const link = document.createElement("a");
      link.href = response.signedUrl;
      link.click();
      openSnackbar("Order Invoice downloaded successfully!", "success");
    })
    .catch((error) => {
      openSnackbar(error.message || "Error fetching Order Invoice.", "error");
    });
};

const handleMarkChequeReceived = (
  openSnackbar,
  selectedPaymentId,
  markChequeReceived,
  setDialogContent
) => {
  const cashierCRNId = localStorage.getItem("cashierCRNId");
  if (!cashierCRNId) {
    openSnackbar("Something went wrong.", "error");
    return;
  }
  const payload = {
    payment_id: selectedPaymentId,
    marked_by: cashierCRNId,
    updated_cheque_status_id: CHEQUE_STATUS_IDS.VERIFIED,
  };
  markChequeReceived(payload, {
    onSuccess: (response) => {
      setDialogContent(null);
      openSnackbar("Cheque Received", "success");
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
    onError: (error) => {
      openSnackbar(`${error}`, "error");
    },
  });
};

const handleOnClickChequeReceived = (
  payment,
  setSelectedPaymentId,
  setDialogContent,
  openSnackbar,
  markChequeReceived,
  isMarkChequeReceivedLoading
) => {
  setSelectedPaymentId(payment.id);
  setDialogContent(
    <div className={styles.dialogContent}>
      <span className={styles.dialogMessage}>
        "Are you sure you want to mark the cheque as received? This action
        cannot be undone."
      </span>
      <div className={styles.dialogActions}>
        <SecondaryButton onClick={handleDialogClose(setDialogContent)}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={() =>
            handleMarkChequeReceived(
              openSnackbar,
              payment.id,
              markChequeReceived,
              setDialogContent
            )
          }
        >
          {isMarkChequeReceivedLoading
            ? "Marking Cheque Received"
            : "Mark Cheque Received"}
        </PrimaryButton>
      </div>
    </div>
  );
};

const getReferenceNumber = (payment) => {
  if (
    payment.cheque_details &&
    Object.keys(payment.cheque_details).length !== 0
  ) {
    return payment.cheque_details.reference_number;
  }

  if (
    payment.transaction_details &&
    Object.keys(payment.transaction_details).length !== 0
  ) {
    const successfulPayment = payment.transaction_details.payment_attempts.find(
      (transaction) =>
        transaction.status.id === SUCCESSFUL_ONLINE_TRANSACTION_STATUS_ID
    );
    return successfulPayment ? successfulPayment.payment_id : "N/A";
  }

  if (payment.loan_details && Object.keys(payment.loan_details).length !== 0) {
    return payment.loan_details.loan_id;
  }

  if (
    payment.bank_transfer_details &&
    Object.keys(payment.bank_transfer_details).length !== 0
  ) {
    return payment.bank_transfer_details.reference_transaction_id;
  }

  return "N/A";
};

const getBankDetailNumber = (payment, banks) => {
  if (
    payment.cheque_details &&
    Object.keys(payment.cheque_details).length !== 0
  ) {
    return payment.cheque_details.bank.name;
  }

  if (
    payment.bank_transfer_details &&
    Object.keys(payment.bank_transfer_details).length !== 0 &&
    payment.bank_transfer_details.bank_id !== null
  ) {
    const bankId = payment.bank_transfer_details.bank_id;
    const bank = banks.find((b) => b.id === bankId);

    const bankName = bank ? bank.name : "N/A";
    return bankName;
  }
  return "N/A";
};

const handleOnClickDownloadAcknowledgement = (
  payment,
  fetchChequeAcknowledgement,
  openSnackbar
) => {
  fetchChequeAcknowledgement(payment.id)
    .then((response) => {
      const link = document.createElement("a");
      link.href = response.signedUrl;
      link.click();
      openSnackbar(
        "Cheque Acknowledgement downloaded successfully!",
        "success"
      );
    })
    .catch((error) => {
      openSnackbar(
        error.message || "Error fetching Cheque Acknowledgement.",
        "error"
      );
    });
};
const handleMarkCashAcknowledged = (
  updateCashStatus,
  openSnackbar,
  selectedPayment,
  setDialogContent
) => {
  const cashierCRNId = localStorage.getItem("cashierCRNId");
  if (!cashierCRNId) {
    openSnackbar("Something went wrong.", "error");
    return;
  }
  const payload = {
    payment_id: selectedPayment.id,
    marked_by: cashierCRNId,
    payment_status_id: PAYMENT_STATUS.DISBURSED,
  };
  updateCashStatus(payload, {
    onSuccess: (response) => {
      openSnackbar("Cash acknowledged successfully.", "success");
      setDialogContent(null);
      setTimeout(() => {
        window.location.reload();
      }, POST_SUBMIT_RELOAD_DELAY_IN_MS);
    },
    onError: (error) => {
      openSnackbar(`${error}`, "error");
    },
  });
};
const handleOnClickCashAcknowledgedButton = (
  updateCashStatus,
  openSnackbar,
  payment,
  setDialogContent,
  isUpdateCashStatusLoading
) => {
  setDialogContent(
    <div className={styles.dialogContent}>
      <span className={styles.dialogMessage}>
        "Are you sure you want to mark the cash payment as acknowledged? This
        action cannot be undone."
      </span>
      <div className={styles.dialogActions}>
        <SecondaryButton onClick={() => setDialogContent(null)}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={() =>
            handleMarkCashAcknowledged(
              updateCashStatus,
              openSnackbar,
              payment,
              setDialogContent
            )
          }
        >
          {isUpdateCashStatusLoading ? "Loading..." : "Acknowledge"}
        </PrimaryButton>
      </div>
    </div>
  );
};
const getAction = (
  payment,
  setSelectedPaymentId,
  setDialogContent,
  fetchChequeAcknowledgement,
  openSnackbar,
  markChequeReceived,
  isMarkChequeReceivedLoading,
  updateCashStatus,
  isUpdateCashStatusLoading
) => {
  const chequeStatusId = payment?.cheque_details?.status?.id;
  const cashStatusId = payment?.cash_details.status?.id;

  const isChequeCreated = chequeStatusId === CHEQUE_STATUS_IDS.CREATED;
  const isChequeVerified = chequeStatusId === CHEQUE_STATUS_IDS.VERIFIED;

  const isCashCreated = cashStatusId === CHEQUE_STATUS_IDS.CREATED;

  if (isChequeCreated) {
    return (
      <PrimaryButton
        onClick={() =>
          handleOnClickChequeReceived(
            payment,
            setSelectedPaymentId,
            setDialogContent,
            openSnackbar,
            markChequeReceived,
            isMarkChequeReceivedLoading
          )
        }
      >
        Mark Cheque Received
      </PrimaryButton>
    );
  }

  if (isChequeVerified) {
    return (
      <PrimaryButton
        onClick={() =>
          handleOnClickDownloadAcknowledgement(
            payment,
            fetchChequeAcknowledgement,
            openSnackbar
          )
        }
      >
        Download Acknowledgment
      </PrimaryButton>
    );
  }

  if (isCashCreated) {
    return (
      <PrimaryButton
        onClick={() =>
          handleOnClickCashAcknowledgedButton(
            updateCashStatus,
            openSnackbar,
            payment,
            setDialogContent,
            isUpdateCashStatusLoading
          )
        }
      >
        Acknowledge
      </PrimaryButton>
    );
  }

  return <div className={styles.actionButtons}>N/A</div>;
};

const getPaymentStatusName = (payment) => {
  if (payment.status.id === PAYMENT_STATUS.DISBURSED && payment.method.id === CASH_PAYMENT_METHOD_ID) {
      return "ACKNOWLEDGED";
    }
    return payment.status.name;
  }

const canDownloadTaxInvoice = (payment) => {
  return (
    SUCCESSFUL_PAYMENT_STATUS_IDS.includes(payment?.status.id) &&
    payment?.method.id !== ADJUSTMENT_AGAINST_REFUNDS_PAYMENT_ID
  );
};

const handleDialogOpen = (setDialogContent, content) => () => {
  setDialogContent(content);
};

const getRows = (
  payments,
  setSelectedPaymentId,
  setDialogContent,
  fetchChequeAcknowledgement,
  fetchPaymentReceipt,
  openSnackbar,
  banks,
  markChequeReceived,
  isMarkChequeReceivedLoading,
  updateCashStatus,
  isUpdateCashStatusLoading
) => {
  const sortedPayments = [...payments].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return sortedPayments.map((payment) => ({
    referenceNo: getReferenceNumber(payment),
    amount: formatINRCurrency(payment.amount),
    method: payment.method.name,
    status: getPaymentStatusName(payment),
    payment_date: formatReadableDate(payment.date, { includeTime: true }),
    bank_details: getBankDetailNumber(payment, banks),
    action: (
      <div className={styles.actionButtons}>
        {getAction(
          payment,
          setSelectedPaymentId,
          setDialogContent,
          fetchChequeAcknowledgement,
          openSnackbar,
          markChequeReceived,
          isMarkChequeReceivedLoading,
          updateCashStatus,
          isUpdateCashStatusLoading
        )}
      </div>
    ),
    receipt: isReceiptDownloadable(payment) ? (
      <PrimaryButton
        onClick={() =>
          handleOnClickDownloadPaymentReceipt(
            payment,
            fetchPaymentReceipt,
            openSnackbar
          )
        }
      >
        Download
      </PrimaryButton>
    ) : (
      "N/A"
    ),
    taxInvoice: canDownloadTaxInvoice(payment) ? (
      <PrimaryButton
        onClick={handleDialogOpen(
          setDialogContent,
          <DownloadInvoice
            onClose={handleDialogClose(setDialogContent)}
            paymentId={payment.id}
          />
        )}
      >
        Download
      </PrimaryButton>
    ) : (
      "N/A"
    ),
  }));
};

const handleDialogClose = (setDialogContent) => () => {
  setDialogContent(null);
};
const handleClickGetOrderSummaryPDFButton = (
  orderId,
  refetchOrderSummaryPDF,
  openSnackbar
) => {
  if (!!orderId) {
    refetchOrderSummaryPDF()
      .then((response) => {
        if (response && response.status === "success") {
          const order_summary_response = response.data;
          if (!!order_summary_response.message) {
            openSnackbar(order_summary_response.message);
          }
          if (!!order_summary_response.signed_url) {
            const link = document.createElement("a");
            link.href = order_summary_response.signed_url;
            link.download = `order_summary_${orderId}.pdf`;
            link.click();
            openSnackbar("Order summary downloaded successfully!", "success");
          }
        } else {
          openSnackbar(
            response.error.message || "Error fetching Order Summary"
          );
        }
      })
      .catch((error) => {
        openSnackbar("Error fetching Order Summary");
      });
  }
};

const OrderDetails = () => {
  const [searchParams] = useSearchParams();
  const [dialogContent, setDialogContent] = useState(null);
  const { openSnackbar } = useSnackbarContext();

  const orderId = searchParams.get("order_id");

  const [selectedPaymentId, setSelectedPaymentId] = useState("");

  const { fetchPaymentReceipt } = useFetchPaymentReceipt();
  const { fetchOrderInvoice } = useFetchOrderInvoice();
  const {
    isLoading: isOrderSummaryPDFLoading,
    refetch: refetchOrderSummaryPDF,
  } = useFetchOrderSummaryPDF(orderId);

  const {
    isPending: isOrderDetailsLoading,
    data: orderDetails,
    isError: isErrorOnOrderDetails,
    error: errorMessageOnOrderDetails,
  } = useFetchOrderDetails(orderId);

  const {
    isPending: isFetchBanksLoading,
    data: banks,
    isError: isErrorOnFetchBanks,
    error: errorMessageOnFetchBanks,
  } = useFetchAllBanks();

  const { mutate: markChequeReceived, isPending: isMarkChequeReceivedLoading } =
    useMarkChequeReceived();

  const { fetchChequeAcknowledgement } = useFetchChequeAcknowledgement();

  const { mutate: updateCashStatus, isPending: isUpdateCashStatusLoading } =
    useUpdateCashStatus();

  if (isOrderDetailsLoading) {
    return <Loader message="Fetching Order details..." />;
  }

  if (isFetchBanksLoading) {
    return <Loader />;
  }

  if (isErrorOnOrderDetails) {
    return <ErrorMessage>{errorMessageOnOrderDetails.message}</ErrorMessage>;
  }

  if (isErrorOnFetchBanks) {
    return <ErrorMessage>{errorMessageOnFetchBanks.message}</ErrorMessage>;
  }

  const payments = orderDetails.payment_details;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.orderHeaderContainer}>
        <div className={styles.heading}>Orders Details</div>
        <div className={styles.actions}>
          {orderDetails.status.id === ORDER_STATUSES.FULLY_PAID &&
            orderDetails.business_unit_id !== SCA_VERTICAL_ID && (
              <PrimaryButton
                onClick={() =>
                  handleOnClickDownloadOrderInvoice(
                    orderDetails.order_id,
                    fetchOrderInvoice,
                    openSnackbar
                  )
                }
              >
                Download Invoice
              </PrimaryButton>
            )}
          <PrimaryButton
            disabled={isOrderSummaryPDFLoading}
            onClick={() =>
              handleClickGetOrderSummaryPDFButton(
                orderDetails.order_id,
                refetchOrderSummaryPDF,
                openSnackbar
              )
            }
          >
            Download Order Summary
          </PrimaryButton>
        </div>
      </div>
      <div className={styles.OrderDetails}>
        <DetailDisplay title="Order Id" value={orderDetails.order_id} />
        {orderDetails.application_number != null && (
          <DetailDisplay
            title="Application No"
            value={orderDetails.application_number}
          />
        )}
        <DetailDisplay
          title="Billing Name"
          value={orderDetails.address_details.billing_address.recipient_name}
        />
        <DetailDisplay
          title="Billing Phone Number"
          value={orderDetails.address_details.billing_address.masked_phone_no}
        />
        <DetailDisplay
          title="Total Order Amount"
          value={formatINRCurrency(orderDetails.amount)}
        />
        <DetailDisplay
          title="Pending Amount"
          value={formatINRCurrency(
            orderDetails.inr_conversion_amount -
              orderDetails.inr_conversion_amount_paid
          )}
        />
        <DetailDisplay
          title="Order Punch Date"
          value={new Date(orderDetails.order_created_date).toLocaleDateString()}
        />
      </div>
      <div className={styles.paymentHeaderContainer}>Payments</div>
      {payments.length > 0 ? (
        <Table
          columns={getColumns()}
          rows={getRows(
            payments,
            setSelectedPaymentId,
            setDialogContent,
            fetchChequeAcknowledgement,
            fetchPaymentReceipt,
            openSnackbar,
            banks,
            markChequeReceived,
            isMarkChequeReceivedLoading,
            updateCashStatus,
            isUpdateCashStatusLoading
          )}
        />
      ) : (
        <ErrorMessage>No payments found for this Order.</ErrorMessage>
      )}
      <Dialog
        open={dialogContent !== null}
        handleClose={handleDialogClose(setDialogContent)}
      >
        {dialogContent}
      </Dialog>
    </div>
  );
};

export default OrderDetails;
